import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext, DragEndEvent, DragOverlay, DragStartEvent, KeyboardSensor,
  PointerSensor, useDroppable, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  Box, Button, Card, CardContent, Stack, Tab, Tabs, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import type { Board, BoardCard, BoardColumn, Stage } from '../../api/types';
import { STAGE_ORDER } from '../../api/types';
import { formatTotals } from '../common/format';
import { EmptyState } from '../common/Common';
import { useThemeMode } from '../../theme/ThemeModeProvider';
import { OpportunityCard, Density } from './OpportunityCard';

interface Props {
  board: Board;
  density: Density;
  onMove: (card: BoardCard, from: Stage, to: Stage) => void;
  onNewOpportunity: (stage: Stage) => void;
  /** Rendered below the grid at ≥600 px, and inside the Closed tab on a phone. */
  historical?: ReactNode;
}

const COLUMN_WIDTH = 300;

const EMPTY_COLUMN = (stage: Stage): BoardColumn =>
  ({ stage, totalValueByCurrency: {}, weightedValueByCurrency: {}, cards: [] });

export function KanbanBoard({ board, density, onMove, onNewOpportunity, historical }: Props) {
  const theme = useTheme();
  const { t } = useTranslation();
  // Three deliberate modes: grid, scroll-snap row, and a stage-filtered list on phones.
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));   // >= 900
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));   // < 600

  const [activeCard, setActiveCard] = useState<BoardCard | null>(null);
  const [activeStage, setActiveStage] = useState<Stage>('lead');
  const [tab, setTab] = useState<Stage>('lead');

  const sensors = useSensors(
    // 8px activation distance so a click still opens the card rather than starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const columnFor = (stage: Stage): BoardColumn =>
    board.columns.find((c) => c.stage === stage) ?? EMPTY_COLUMN(stage);

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as { card: BoardCard; stage: Stage } | undefined;
    if (data) {
      setActiveCard(data.card);
      setActiveStage(data.stage);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const card = activeCard;
    const from = activeStage;
    setActiveCard(null);
    if (!card || !event.over) return;

    const to = event.over.id as Stage;
    if (to !== from) onMove(card, from, to);
  };

  if (isPhone) {
    const column = columnFor(tab);
    return (
      <Box>
        <Tabs
          value={tab}
          onChange={(_, v: Stage) => setTab(v)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          {STAGE_ORDER.map((stage) => (
            <Tab
              key={stage}
              value={stage}
              label={`${t(`stage.${stage}`)} (${columnFor(stage).cards.length})`}
              data-testid={`stage-tab-${stage}`}
            />
          ))}
        </Tabs>

        <Stack spacing={1.5} data-testid={`board-column-${tab}`}>
          {column.cards.length === 0 ? (
            <EmptyState title={t('board.noStageOpportunities', { stage: t(`stage.${tab}`) })} />
          ) : (
            column.cards.map((card) => (
              <OpportunityCard
                key={card.id}
                card={card}
                stage={tab}
                draggable={false}
                density={density}
                onMoveTo={(c, to) => onMove(c, tab, to)}
              />
            ))
          )}
          <Button startIcon={<AddIcon />} onClick={() => onNewOpportunity(tab)} data-testid={`new-opp-${tab}`}>
            {t('board.newOpportunity')}
          </Button>
        </Stack>

        {/* An archive of closed deals belongs with the closed ones, not above the Lead list. */}
        {tab === 'closed' && historical}
      </Box>
    );
  }

  return (
    <>
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <Box
          data-testid="board-scroller"
          sx={{
            display: isDesktop ? 'grid' : 'flex',
            gridTemplateColumns: isDesktop ? 'repeat(4, minmax(0, 1fr))' : undefined,
            gap: 2,
            overflowX: isDesktop ? 'visible' : 'auto',
            scrollSnapType: isDesktop ? undefined : 'x mandatory',
            pb: 1,
          }}
        >
          {STAGE_ORDER.map((stage) => (
            <Column
              key={stage}
              column={columnFor(stage)}
              density={density}
              fixedWidth={!isDesktop}
              onMoveTo={(card, to) => onMove(card, stage, to)}
              onNew={() => onNewOpportunity(stage)}
            />
          ))}
        </Box>

        <DragOverlay>
          {activeCard && (
            <Box sx={{ transform: 'rotate(3deg)', boxShadow: '0 8px 24px rgba(0,0,0,0.18)', borderRadius: '10px' }}>
              <Card>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="h3" noWrap>{activeCard.title}</Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>{activeCard.customerName}</Typography>
                </CardContent>
              </Card>
            </Box>
          )}
        </DragOverlay>
      </DndContext>

      {/* Full page width, outside the horizontal scroller, so its table never widens the board. */}
      {historical}
    </>
  );
}

function Column({
  column, density, fixedWidth, onMoveTo, onNew,
}: {
  column: BoardColumn;
  density: Density;
  fixedWidth: boolean;
  onMoveTo: (card: BoardCard, to: Stage) => void;
  onNew: () => void;
}) {
  const { t } = useTranslation();
  const { stageColors } = useThemeMode();
  const { setNodeRef, isOver } = useDroppable({ id: column.stage });
  const accent = stageColors[column.stage === 'closed' ? 'won' : column.stage];
  const hasForecast = Object.keys(column.weightedValueByCurrency ?? {}).length > 0;

  return (
    <Box
      ref={setNodeRef}
      data-testid={`board-column-${column.stage}`}
      sx={{
        minWidth: fixedWidth ? COLUMN_WIDTH : 0,
        width: fixedWidth ? COLUMN_WIDTH : 'auto',
        flexShrink: 0,
        scrollSnapAlign: fixedWidth ? 'start' : undefined,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: isOver ? 'primary.main' : 'divider',
        borderTop: `3px solid ${accent}`,
        borderRadius: '10px',
        p: 1.5,
        transition: 'border-color 120ms ease',
      }}
    >
      <Stack sx={{ mb: 1.5, px: 0.5 }}>
        <Typography variant="h3">
          {t(`stage.${column.stage}`)}
          {column.stage === 'closed' && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 0.5 }}
              data-testid="closed-window-label"
            >
              {t('board.closedWindow')}
            </Typography>
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary" data-testid={`board-total-${column.stage}`}>
          {column.cards.length} · {formatTotals(column.totalValueByCurrency)}
        </Typography>
        {hasForecast && (
          <Tooltip title={t('board.forecastTooltip')}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontStyle: 'italic' }}
              data-testid={`board-forecast-${column.stage}`}
            >
              {t('board.forecast', { value: formatTotals(column.weightedValueByCurrency) })}
            </Typography>
          </Tooltip>
        )}
      </Stack>

      <Stack spacing={density === 'compact' ? 1 : 1.5} sx={{ minHeight: 80 }}>
        {column.cards.map((card) => (
          <OpportunityCard
            key={card.id}
            card={card}
            stage={column.stage}
            draggable
            density={density}
            onMoveTo={onMoveTo}
          />
        ))}
        {column.cards.length === 0 && (
          <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 3 }}>
            {t('board.nothingHere')}
          </Typography>
        )}
      </Stack>

      <Button
        fullWidth
        size="small"
        startIcon={<AddIcon />}
        onClick={onNew}
        sx={{ mt: 1.5, justifyContent: 'flex-start', color: 'text.secondary' }}
        data-testid={`new-opp-${column.stage}`}
      >
        {t('board.newOpportunity')}
      </Button>
    </Box>
  );
}
